import React, { useState } from "react";
import styles from "../WidgetAnalytics.module.css";
import {
  FiSearch, FiChevronLeft, FiChevronRight, FiX,
  FiMonitor, FiSmartphone, FiTablet, FiActivity, FiMoreVertical,
} from "react-icons/fi";

const DeviceIcon = ({ device }) => {
  const d = (device || "").toLowerCase();
  if (d === "mobile")  return <FiSmartphone className={styles.deviceIcon} />;
  if (d === "tablet")  return <FiTablet     className={styles.deviceIcon} />;
  return <FiMonitor className={styles.deviceIcon} />;
};

const SkeletonTelemetry = () => (
  <div className={`${styles.skeletonSectionCard} mt-4`}>
    <div className={styles.skeletonSectionHeader}>
      <div>
        <div className={styles.skeleton} style={{ height: "14px", width: "160px", marginBottom: "6px" }} />
        <div className={styles.skeleton} style={{ height: "10px", width: "320px" }} />
      </div>
    </div>

    <div className={styles.skeletonFiltersBar}>
      <div className={styles.skeleton} style={{ height: "36px", width: "160px", borderRadius: "10px" }} />
      <div className={styles.skeletonTabRow}>
        {[60, 80, 90].map((w, i) => (
          <div key={i} className={styles.skeleton} style={{ height: "36px", width: w, borderRadius: "8px" }} />
        ))}
      </div>
      <div className={styles.skeleton} style={{ height: "36px", width: "120px", borderRadius: "8px" }} />
    </div>

    <div className={styles.skeletonPerPageBar}>
      <div className={styles.skeleton} style={{ height: "32px", width: "110px", borderRadius: "8px" }} />
    </div>

    <div className={styles.skeletonTableHeader}>
      {[60, 100, 90, 70, 65, 70, 85, 100, 90, 80, 80].map((w, i) => (
        <div key={i} className={styles.skeleton} style={{ width: w, height: "10px", flexShrink: 0 }} />
      ))}
    </div>

    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className={styles.skeletonTableRow}>
        <div className={styles.skeleton} style={{ width: "62px",  height: "13px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "110px", height: "13px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "44px",  height: "20px", borderRadius: "20px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "65px",  height: "13px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "30px",  height: "13px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "34px",  height: "13px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "34px",  height: "13px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "34px",  height: "13px", flexShrink: 0 }} />
        <div className={styles.skeletonLastSeenCol}>
          <div className={styles.skeleton} style={{ height: "12px", width: "100%" }} />
          <div className={styles.skeleton} style={{ height: "10px", width: "80%" }} />
        </div>
        <div className={styles.skeleton} style={{ width: "72px", height: "22px", borderRadius: "20px", flexShrink: 0 }} />
        <div className={styles.skeletonActionRow}>
          <div className={styles.skeleton} style={{ width: "82px", height: "28px", borderRadius: "8px" }} />
          <div className={styles.skeleton} style={{ width: "30px", height: "28px", borderRadius: "8px" }} />
        </div>
      </div>
    ))}

    <div className={styles.skeletonFooterRow}>
      <div className={styles.skeleton} style={{ height: "12px", width: "130px" }} />
      <div className={styles.skeletonPagRow}>
        {[32, 32, 32, 32].map((w, i) => (
          <div key={i} className={styles.skeleton} style={{ width: w, height: "32px", borderRadius: "8px" }} />
        ))}
      </div>
    </div>
  </div>
);

const TelemetryList = ({
  visitors,
  onFetchJourney,
  loading,
  page,
  perPage,
  totalCount,
  status,
  device,
  onPageChange,
  onPerPageChange,
  onSearchChange,
  onStatusChange,
  onDeviceChange,
}) => {
  const [searchVal,      setSearchVal]      = useState("");
  const [journeyVisitor, setJourneyVisitor] = useState(null);
  const [journeyEvents,  setJourneyEvents]  = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const handleViewJourney = async (v) => {
    setJourneyVisitor(v);
    setJourneyEvents([]);
    setJourneyLoading(true);
    const { events, visitorInfo } = await onFetchJourney(v.id);
    setJourneyEvents(events || []);
    if (visitorInfo) {
      setJourneyVisitor((prev) => ({ ...prev, ...visitorInfo }));
    }
    setJourneyLoading(false);
  };

  const handleSearchChange = (val) => {
    setSearchVal(val);
    onSearchChange(val);
  };

  if (loading) return <SkeletonTelemetry />;

  const totalPages  = Math.ceil(totalCount / perPage) || 1;
  const startRecord = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const endRecord   = Math.min(page * perPage, totalCount);

  const pageNumbers = [];
  const maxVisible  = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
  let endPage   = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

  return (
    <>
      <div className={`${styles.sectionCard} mt-4`}>
        <div className={styles.sectionCardHeader}>
          <div>
            <div className={styles.sectionTitle}>Visitors List</div>
            <div className={styles.sectionSubtitle}>
              Real-time visitor sessions, device distribution and interaction journey logs
            </div>
          </div>
        </div>

        <div className={styles.telemetryFilters}>
          <div className={styles.searchBox}>
            <FiSearch />
            <input
              placeholder="Search telemetry..."
              value={searchVal}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className={styles.tabGroup}>
            {["all", "anonymous", "identified"].map((t) => (
              <button
                key={t}
                className={`${styles.tabBtn} ${status === t ? styles.tabBtnActive : ""}`}
                onClick={() => onStatusChange(t)}
              >
                {t.toUpperCase()}
              </button>
            ))}
          </div>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>DEVICE:</span>
            <select
              className={styles.filterSelect}
              value={device}
              onChange={(e) => onDeviceChange(e.target.value)}
            >
              <option>All</option>
              <option>Mobile</option>
              <option>Desktop</option>
              <option>Tablet</option>
            </select>
          </div>
        </div>

        <div className={styles.perPageRow}>
          <select
            className={styles.perPageSelect}
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
          </select>
        </div>

        <div className={styles.tableResponsive}>
          <table className={styles.telemetryTable}>
            <thead>
              <tr>
                <th>VISITOR ID</th>
                <th>MOBILE NUMBER</th>
                <th>CHAT STARTED</th>
                <th>DEVICE</th>
                <th>SESSIONS</th>
                <th>PAGE VIEWS</th>
                <th>WIDGET OPENS</th>
                <th>WHATSAPP CLICKS</th>
                <th>LAST SEEN</th>
                <th>STATUS</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {visitors.length === 0 ? (
                <tr>
                  <td colSpan={11} className={styles.tableEmptyCell}>No visitors found</td>
                </tr>
              ) : (
                visitors.map((v, i) => (
                  <tr key={i}>
                    <td><span className={styles.visitorId}>{v.id}</span></td>
                    <td>
                      {v.mobile !== "–"
                        ? <span className={styles.mobileNum}>{v.mobile}</span>
                        : <span className={styles.mobileNone}>–</span>}
                    </td>
                    <td>
                      {v.chatStarted
                        ? <span className={styles.chatYes}>+ Yes</span>
                        : <span className={styles.chatNo}>- No</span>}
                    </td>
                    <td><span className={styles.deviceCell}><DeviceIcon device={v.device} />{v.device}</span></td>
                    <td>{v.sessions}</td>
                    <td>{v.pageViews}</td>
                    <td><span className={styles.widgetOpenValue}>{v.widgetOpens}</span></td>
                    <td>{v.waClicks}</td>
                    <td>
                      <div className={styles.lastSeenAgo}>{v.lastSeen}</div>
                      <div className={styles.lastSeenDate}>{v.lastSeenDate}</div>
                    </td>
                    <td>
                      <span className={v.status === "Identified" ? styles.badgeIdentified : styles.badgeAnonymous}>
                        + {v.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionBtns}>
                        <button className={styles.viewJourneyBtn} onClick={() => handleViewJourney(v)}>
                          View Journey
                        </button>
                        <button className={styles.moreBtn}><FiMoreVertical /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.tableFooter}>
          <span className={styles.tableFooterInfo}>
            {totalCount === 0
              ? "No visitors found"
              : `Showing ${startRecord} to ${endRecord} of ${totalCount} visitors`}
          </span>
          <div className={styles.pagBtns}>
            <button
              className={styles.pagIconBtn}
              disabled={page === 1}
              onClick={() => onPageChange(page - 1)}
            >
              <FiChevronLeft />
            </button>

            {pageNumbers.map((n) => (
              <button
                key={n}
                className={`${styles.pagBtn} ${page === n ? styles.pagBtnActive : ""}`}
                onClick={() => onPageChange(n)}
              >
                {n}
              </button>
            ))}

            <button
              className={styles.pagIconBtn}
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* SESSION JOURNEY MODAL */}
      {journeyVisitor && (
        <div className={styles.modalOverlay} onClick={() => setJourneyVisitor(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalInner}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIconWrap}><FiActivity /></div>
              <div className={styles.modalHeaderBody}>
                <div className={styles.modalTitleRow}>
                  <span className={styles.modalTitle}>Session Journey Logs</span>
                  <span className={journeyVisitor.status === "Identified" ? styles.modalBadgeIdentified : styles.modalBadgeAnon}>
                    {journeyVisitor.status}
                  </span>
                </div>
                <div className={styles.modalSubtitle}>
                  Detailed step-by-step telemetry of <strong>{journeyVisitor.id}</strong>
                </div>
              </div>
              <button className={styles.modalClose} onClick={() => setJourneyVisitor(null)}><FiX /></button>
            </div>

            <hr className={styles.modalDivider} />

            <div className={styles.modalSummary}>
              <div className={styles.modalSummaryItem}>
                <div className={styles.modalSummaryLabel}>MOBILE NUMBER</div>
                <div className={styles.modalSummaryValue}>{journeyVisitor.mobile}</div>
              </div>
              <div className={styles.modalSummaryItem}>
                <div className={styles.modalSummaryLabel}>CHAT STARTED</div>
                <div className={styles.modalSummaryValue} style={{ color: journeyVisitor.chatStarted ? "#16a34a" : "#ef4444" }}>
                  {journeyVisitor.chatStarted ? "Yes" : "No"}
                </div>
              </div>
              <div className={styles.modalSummaryItem}>
                <div className={styles.modalSummaryLabel}>DEVICE</div>
                <div className={styles.modalSummaryValue}>
                  <DeviceIcon device={journeyVisitor.device} />{journeyVisitor.device}
                </div>
              </div>
              <div className={styles.modalSummaryItem}>
                <div className={styles.modalSummaryLabel}>TOTAL SESSIONS</div>
                <div className={styles.modalSummaryValue}>{journeyVisitor.sessions}</div>
              </div>
              <div className={styles.modalSummaryItem}>
                <div className={styles.modalSummaryLabel}>WHATSAPP CLICKS</div>
                <div className={styles.modalSummaryValue}>{journeyVisitor.waClicks}</div>
              </div>
            </div>

            <div className={styles.activityLabel}>ACTIVITY TIMELINE</div>

            {journeyLoading ? (
              <div className={styles.journeyLoading}>Loading timeline…</div>
            ) : journeyEvents.length === 0 ? (
              <div className={styles.journeyEmpty}>No events recorded for this visitor.</div>
            ) : (
              <div className={styles.timeline}>
                {journeyEvents.map((step, i) => (
                  <div key={i} className={styles.timelineItem}>
                    <div className={styles.timelineLeft}>
                      <div className={styles.timelineDot} style={{ borderColor: step.color, background: `${step.color}18`, boxShadow: `0 0 0 3px ${step.color}22` }} />
                      {i < journeyEvents.length - 1 && <div className={styles.timelineLine} />}
                    </div>
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineEvent}>{step.event}</div>
                      <div className={styles.timelinePageRow}>
                        <span className={styles.timelinePageLabel}>Page:</span>
                        <span className={styles.timelinePage}>{step.page}</span>
                      </div>
                    </div>
                    <div className={styles.timelineTime}>🕐 {step.time}</div>
                  </div>
                ))}
              </div>
            )}

            <hr className={styles.modalDivider} />

            <div className={styles.modalFooterRow}>
              <button className={styles.closeJourneysBtn} onClick={() => setJourneyVisitor(null)}>
                Close Journeys
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TelemetryList;
